// frontend/src/modules/workspace/api.ts
//
// Thin client over /api/workspace — tenant-side Access management.
// All calls go through the shared api() wrapper (Bearer auth + ApiError on
// non-2xx). Mirrors the modules/monitoring/api.ts style.

import { api } from '../../core/api-client';
import type {
  WorkspaceMember, WorkspaceRole, WorkspacePermission,
  InviteMemberInput, InviteMemberResult, ResendInviteResult,
  CreateRoleInput, UpdateRoleInput, AuditEntry,
} from './types';

export const workspaceApi = {
  // Members
  listMembers: () =>
    api<{ members: WorkspaceMember[] }>('/api/workspace/members').then((r) => r.members),
  inviteMember: (input: InviteMemberInput) =>
    api<InviteMemberResult>('/api/workspace/members', { method: 'POST', body: input }),
  setMemberRoles: (userId: string, roleKeys: string[]) =>
    api<{ member: WorkspaceMember }>(`/api/workspace/members/${userId}/roles`, { method: 'PUT', body: { role_keys: roleKeys } }).then((r) => r.member),
  setMemberActive: (userId: string, isActive: boolean) =>
    api<{ member: WorkspaceMember }>(`/api/workspace/members/${userId}`, { method: 'PATCH', body: { is_active: isActive } }).then((r) => r.member),
  resendInvite: (userId: string) =>
    api<ResendInviteResult>(`/api/workspace/members/${userId}/resend-invite`, { method: 'POST', body: {} }),

  // Roles
  listRoles: () =>
    api<{ roles: WorkspaceRole[] }>('/api/workspace/roles').then((r) => r.roles),
  createRole: (input: CreateRoleInput) =>
    api<{ role: WorkspaceRole }>('/api/workspace/roles', { method: 'POST', body: input }).then((r) => r.role),
  updateRole: (id: string, patch: UpdateRoleInput) =>
    api<{ role: WorkspaceRole }>(`/api/workspace/roles/${id}`, { method: 'PATCH', body: patch }).then((r) => r.role),
  setRolePermissions: (id: string, permissions: string[]) =>
    api<{ role: WorkspaceRole }>(`/api/workspace/roles/${id}/permissions`, { method: 'PUT', body: { permissions } }).then((r) => r.role),
  deleteRole: (id: string) =>
    api<{ ok: true }>(`/api/workspace/roles/${id}`, { method: 'DELETE' }),

  // Permission catalog (already restricted to tenant's enabled modules + access)
  listPermissions: () =>
    api<{ permissions: WorkspacePermission[] }>('/api/workspace/permissions').then((r) => r.permissions),

  // Audit log (read-only; this tenant's rows only)
  listAudit: () =>
    api<{ entries: AuditEntry[] }>('/api/workspace/audit').then((r) => r.entries),
};
